import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, createSearchParams } from "react-router-dom";

import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Badge from "@mui/material/Badge";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import shortLogo from "assets/images/logos/ShortLogo.png";
import { useCart } from "context/CartContext";
import { useAuth } from "context/AuthContext";
import { useStorefrontTenant } from "context/StorefrontTenantContext";
import { isDistributorRole, isPortalRole } from "lib/portalRoles";
import { storefrontThemeForTenant } from "lib/storefrontTheme";
import useStorefrontCopy from "hooks/useStorefrontCopy";

function toKey(v) {
  return String(v || "")
    .trim()
    .toLowerCase();
}

export default function StorefrontLayout({
  children,
  searchPlaceholder,
  maxWidth,
  hideHeader,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { itemCount } = useCart();
  const { token, pluginRole } = useAuth();
  const { tenant } = useStorefrontTenant();
  const { t, locale } = useStorefrontCopy();

  const role = toKey(pluginRole);
  const portalUser = isPortalRole(role);
  const distributor = isDistributorRole(role);
  const isAuthed = !!token;
  const theme = storefrontThemeForTenant(tenant, locale);
  const brandName = theme.name || t("common.brandFallback", "Storefront");
  const brandLogo = theme.logoUrl || shortLogo;
  const accent = theme.accent;

  const isMobileRoute =
    location.pathname === "/m" || location.pathname.startsWith("/m/");
  const isHomePage = location.pathname === "/" || location.pathname === "/m";
  const toPath = (path) =>
    isMobileRoute ? `/m${path === "/" ? "" : path}` : path;

  const qFromUrl = useMemo(() => {
    try {
      const p = new URLSearchParams(location.search || "");
      return String(p.get("q") || "");
    } catch {
      return "";
    }
  }, [location.search]);

  const [q, setQ] = useState(qFromUrl);

  useEffect(() => {
    setQ(qFromUrl);
  }, [qFromUrl]);

  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);

  const submitSearch = (value) => {
    const next = String(value || "").trim();
    const params = next ? createSearchParams({ q: next }).toString() : "";
    navigate(`${toPath("/catalog")}${params ? `?${params}` : ""}`);
  };

  const onChangeQ = (value) => {
    const next = String(value || "");
    setQ(next);
    // If already on catalog, update query param live.
    if (location.pathname === toPath("/catalog")) {
      const trimmed = next.trim();
      const params = new URLSearchParams(location.search || "");
      if (trimmed) params.set("q", trimmed);
      else params.delete("q");
      navigate(
        `${toPath("/catalog")}${
          params.toString() ? `?${params.toString()}` : ""
        }`,
        { replace: true },
      );
    }
  };

  const headerTextButtonSx = {
    color: theme.ink,
    borderRadius: `${theme.buttonRadius}px`,
    px: 1.6,
    py: 0.8,
    fontSize: "0.82rem",
    fontWeight: 700,
    letterSpacing: "0.02em",
    "&:hover": {
      bgcolor: `${accent}12`,
      color: accent,
    },
  };

  const goToSessionDestination = () => {
    if (!isAuthed) {
      navigate("/authentication/sign-in");
      return;
    }
    if (portalUser) {
      navigate(distributor ? "/account/valve-bodies" : "/account");
      return;
    }
    navigate("/post-login");
  };

  const pageBody =
    maxWidth === null ? (
      children
    ) : (
      <Container maxWidth={maxWidth} sx={{ py: 4 }}>
        {children}
      </Container>
    );

  if (!isMobileRoute && !hideHeader && theme.preset === "industrial") {
    return (
      <Box
        data-storefront-theme={theme.preset}
        sx={{
          minHeight: "100vh",
          display: "grid",
          gridTemplateColumns: "88px minmax(0, 1fr)",
          bgcolor: "#080B0E",
          "--storefront-accent": accent,
          "--storefront-ink": theme.ink,
          "--storefront-muted": theme.muted,
          "--storefront-line": theme.line,
          "--storefront-canvas": theme.canvas,
          "--storefront-surface": theme.surface,
          "--storefront-card-radius": `${theme.cardRadius}px`,
          "--storefront-panel-radius": `${theme.panelRadius}px`,
        }}
      >
        <Box
          sx={{
            position: "sticky",
            top: 0,
            height: "100vh",
            zIndex: 20,
            bgcolor: "#050608",
            color: "#fff",
            display: "grid",
            gridTemplateRows: "auto 1fr auto",
            justifyItems: "center",
            py: 2,
            boxShadow: "18px 0 60px rgba(0,0,0,0.34)",
          }}
        >
          <MDBox
            component="button"
            type="button"
            onClick={() => navigate(toPath("/"))}
            sx={{
              width: 50,
              height: 50,
              border: 0,
              p: 0,
              bgcolor: "transparent",
              cursor: "pointer",
            }}
          >
            <Box
              component="img"
              src={brandLogo}
              alt={brandName}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                filter: theme.logoUrl ? "none" : "brightness(0) invert(1)",
              }}
            />
          </MDBox>

          <Stack spacing={1.5} alignItems="center" justifyContent="center">
            {[
              ["precision_manufacturing", toPath("/"), "Home"],
              [
                "manage_search",
                toPath("/catalog"),
                t("common.catalog", "Catalog"),
              ],
              [
                "shopping_cart_checkout",
                toPath("/cart"),
                t("common.cart", "Cart"),
              ],
            ].map(([icon, path, label]) => (
              <IconButton
                key={label}
                aria-label={label}
                onClick={() => navigate(path)}
                sx={{
                  width: 48,
                  height: 48,
                  color: "rgba(255,255,255,0.70)",
                  borderRadius: 0,
                  "&:hover": {
                    color: "#fff",
                    bgcolor: "rgba(255,255,255,0.08)",
                  },
                }}
              >
                {label === t("common.cart", "Cart") ? (
                  <Badge
                    color="error"
                    badgeContent={itemCount || 0}
                    invisible={!itemCount}
                  >
                    <Icon>{icon}</Icon>
                  </Badge>
                ) : (
                  <Icon>{icon}</Icon>
                )}
              </IconButton>
            ))}
          </Stack>

          <IconButton
            aria-label={
              isAuthed
                ? distributor
                  ? t("common.portal", "Portal")
                  : t("common.account", "Account")
                : t("common.signIn", "Sign in")
            }
            onClick={goToSessionDestination}
            sx={{
              width: 48,
              height: 48,
              color: "#fff",
              borderRadius: 0,
              bgcolor: accent,
              "&:hover": { bgcolor: accent, filter: "brightness(0.92)" },
            }}
          >
            <Icon>{isAuthed ? "person" : "login"}</Icon>
          </IconButton>
        </Box>

        <Box
          sx={{
            minWidth: 0,
            position: "relative",
            background:
              "radial-gradient(circle at 78% 0%, rgba(181,71,8,0.18), transparent 34%), linear-gradient(135deg, #080B0E 0%, #111820 48%, #080B0E 100%)",
            "&:before": {
              content: '""',
              position: "fixed",
              inset: 0,
              pointerEvents: "none",
              opacity: 0.12,
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.12) 1px, transparent 1px)",
              backgroundSize: "54px 54px",
              maskImage:
                "linear-gradient(180deg, rgba(0,0,0,0.78), transparent 70%)",
            },
          }}
        >
          <Box
            sx={{
              minHeight: 86,
              px: { xs: 2.5, md: 4 },
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) minmax(220px, 420px) auto",
              gap: 2.5,
              alignItems: "center",
              color: "#fff",
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <MDTypography
                variant="caption"
                sx={{
                  display: "block",
                  color: "rgba(255,255,255,0.50)",
                  fontWeight: 900,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                }}
              >
                {theme.headerTagline || "Technical studio"}
              </MDTypography>
              <MDTypography
                variant="h4"
                sx={{
                  color: "#fff",
                  fontWeight: 950,
                  lineHeight: 1,
                  mt: 0.25,
                }}
              >
                {brandName}
              </MDTypography>
            </Box>

            <TextField
              variant="standard"
              placeholder={searchPlaceholder || t("common.searchPlaceholder")}
              value={q}
              onChange={(e) => onChangeQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitSearch(q);
              }}
              sx={{
                width: "100%",
                "& .MuiInputBase-root": {
                  color: "#fff",
                  fontSize: "0.94rem",
                },
                "& .MuiInput-underline:before": {
                  borderBottomColor: "rgba(255,255,255,0.22)",
                },
                "& .MuiInput-underline:hover:before": {
                  borderBottomColor: "rgba(255,255,255,0.46)",
                },
                "& .MuiInput-underline:after": {
                  borderBottomColor: accent,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon sx={{ color: accent }}>manage_search</Icon>
                  </InputAdornment>
                ),
              }}
            />

            <MDButton
              variant="text"
              sx={{
                bgcolor: "#fff",
                color: "#080B0E",
                borderRadius: 0,
                px: 2.5,
                py: 1,
                fontSize: "0.8rem",
                fontWeight: 950,
                boxShadow: "none",
                "&:hover": {
                  bgcolor: accent,
                  color: "#fff",
                  boxShadow: "none",
                },
              }}
              onClick={() => submitSearch(q)}
            >
              Search
            </MDButton>
          </Box>
          {pageBody}
        </Box>
      </Box>
    );
  }

  if (!isMobileRoute && !hideHeader && theme.preset === "commerce") {
    return (
      <Box
        data-storefront-theme={theme.preset}
        sx={{
          minHeight: "100vh",
          bgcolor: theme.pageBg,
          "--storefront-accent": accent,
          "--storefront-ink": theme.ink,
          "--storefront-muted": theme.muted,
          "--storefront-line": theme.line,
          "--storefront-canvas": theme.canvas,
          "--storefront-surface": theme.surface,
          "--storefront-card-radius": `${theme.cardRadius}px`,
          "--storefront-panel-radius": `${theme.panelRadius}px`,
        }}
      >
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            bgcolor: "rgba(255,255,255,0.86)",
            color: theme.ink,
            borderBottom: `1px solid rgba(16,24,40,0.08)`,
            backdropFilter: "blur(24px)",
          }}
        >
          <Toolbar
            sx={{
              minHeight: 82,
              gap: 2,
              px: { xs: 2, md: 3 },
              maxWidth: 1540,
              width: "100%",
              mx: "auto",
            }}
          >
            <MDBox
              display="flex"
              alignItems="center"
              gap={1.1}
              sx={{
                minWidth: 220,
                cursor: "pointer",
                px: 0,
                py: 0.75,
              }}
              onClick={() => navigate(toPath("/"))}
            >
              <MDBox
                component="img"
                src={brandLogo}
                alt={brandName}
                sx={{
                  height: 46,
                  width: "auto",
                  maxWidth: 82,
                  objectFit: "contain",
                }}
              />
              <MDBox>
                <MDTypography
                  variant="button"
                  sx={{
                    display: "block",
                    color: theme.ink,
                    fontWeight: 950,
                    letterSpacing: 0,
                  }}
                >
                  {brandName}
                </MDTypography>
                <MDTypography
                  variant="caption"
                  sx={{ color: theme.muted, fontWeight: 700 }}
                >
                  {theme.headerTagline}
                </MDTypography>
              </MDBox>
            </MDBox>

            <TextField
              variant="standard"
              placeholder={searchPlaceholder || t("common.searchPlaceholder")}
              value={q}
              onChange={(e) => onChangeQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitSearch(q);
              }}
              sx={{
                flex: 1,
                maxWidth: 520,
                mx: "auto",
                "& .MuiInputBase-root": {
                  color: theme.ink,
                  fontSize: "1rem",
                },
                "& .MuiInput-underline:before": {
                  borderBottomColor: "rgba(16,24,40,0.18)",
                },
                "& .MuiInput-underline:hover:before": {
                  borderBottomColor: "rgba(16,24,40,0.34)",
                },
                "& .MuiInput-underline:after": {
                  borderBottomColor: accent,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon sx={{ color: theme.muted }}>search</Icon>
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="Search"
                      onClick={() => submitSearch(q)}
                      sx={{ color: theme.ink }}
                    >
                      <Icon fontSize="small">arrow_forward</Icon>
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <MDButton
              variant="text"
              sx={{
                ...headerTextButtonSx,
                color: theme.ink,
                borderRadius: 0,
                "&:hover": { bgcolor: "transparent", color: accent },
              }}
              onClick={() => navigate(toPath("/catalog"))}
            >
              {t("common.catalog", "Catalog")}
            </MDButton>
            <IconButton
              aria-label="Cart"
              onClick={() => navigate(toPath("/cart"))}
              sx={{
                width: 44,
                height: 44,
                color: theme.ink,
                "&:hover": { bgcolor: `${accent}14` },
              }}
            >
              <Badge
                color="error"
                badgeContent={itemCount || 0}
                invisible={!itemCount}
              >
                <Icon>shopping_bag</Icon>
              </Badge>
            </IconButton>
            <MDButton
              variant="text"
              sx={{
                bgcolor: theme.ink,
                color: "#fff",
                borderRadius: "999px",
                px: 2,
                py: 0.95,
                fontWeight: 900,
                boxShadow: "none",
                "&:hover": { bgcolor: accent, boxShadow: "none" },
              }}
              onClick={goToSessionDestination}
            >
              {isAuthed
                ? distributor
                  ? t("common.portal", "Portal")
                  : t("common.account", "Account")
                : t("common.signIn", "Sign in")}
            </MDButton>
          </Toolbar>
          <MDBox
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 3,
              px: 3,
              py: 1,
              bgcolor: "transparent",
            }}
          >
            {[
              [t("common.catalog", "Catalog"), "/catalog"],
              [t("home.primaryCta", "Shop products"), "/catalog"],
              [t("support.title", "Support"), "/account"],
            ].map(([label, path]) => (
              <MDButton
                key={label}
                variant="text"
                sx={{
                  color: theme.muted,
                  fontSize: "0.76rem",
                  fontWeight: 900,
                  textTransform: "uppercase",
                  borderRadius: 0,
                  "&:hover": { color: accent, bgcolor: "transparent" },
                }}
                onClick={() => navigate(toPath(path))}
              >
                {label}
              </MDButton>
            ))}
          </MDBox>
        </AppBar>
        {pageBody}
      </Box>
    );
  }

  return (
    <Box
      data-storefront-theme={theme.preset}
      sx={{
        minHeight: "100vh",
        bgcolor: theme.pageBg,
        "--storefront-accent": accent,
        "--storefront-ink": theme.ink,
        "--storefront-muted": theme.muted,
        "--storefront-line": theme.line,
        "--storefront-canvas": theme.canvas,
        "--storefront-surface": theme.surface,
        "--storefront-card-radius": `${theme.cardRadius}px`,
        "--storefront-panel-radius": `${theme.panelRadius}px`,
      }}
    >
      {!isMobileRoute && !hideHeader ? (
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            bgcolor: theme.headerBg,
            color: theme.ink,
            borderBottom: `1px solid ${theme.headerBorder}`,
            backdropFilter: "blur(18px)",
          }}
        >
          <Toolbar
            sx={{
              minHeight: { xs: 68, md: 76 },
              py: 0.75,
              gap: { xs: 1.25, md: 2 },
            }}
          >
            <MDBox
              display="flex"
              alignItems="center"
              sx={{
                flex: { xs: "1 1 auto", md: "0 1 360px" },
                minWidth: 0,
                maxWidth: { md: 420 },
              }}
            >
              <MDBox
                component="img"
                src={brandLogo}
                alt={brandName}
                sx={{
                  height: { xs: 38, md: 44 },
                  width: "auto",
                  maxWidth: { xs: 52, sm: 64, md: 74 },
                  objectFit: "contain",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
                onClick={() => navigate(toPath("/"))}
              />
              <MDBox
                sx={{
                  ml: 1.25,
                  display: { xs: "none", sm: "block" },
                  minWidth: 0,
                }}
              >
                <MDTypography
                  variant="button"
                  sx={{
                    display: "block",
                    color: theme.ink,
                    fontWeight: 800,
                    lineHeight: 1.1,
                    letterSpacing: "0.03em",
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {brandName}
                </MDTypography>
                <MDTypography
                  variant="caption"
                  sx={{
                    color: theme.muted,
                    fontSize: "0.68rem",
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                  }}
                >
                  {theme.headerTagline}
                </MDTypography>
              </MDBox>
            </MDBox>

            <MDBox
              display="flex"
              alignItems="center"
              gap={1}
              sx={{
                flex: { xs: "0 0 auto", md: "1 1 auto" },
                justifyContent: "flex-end",
                minWidth: 0,
                flexWrap: "nowrap",
              }}
            >
              <MDButton
                variant="text"
                sx={headerTextButtonSx}
                onClick={() => navigate(toPath("/catalog"))}
              >
                {t("common.catalog", "Catalog")}
              </MDButton>

              <MDButton
                variant="text"
                sx={headerTextButtonSx}
                onClick={() => navigate(toPath("/cart"))}
                startIcon={
                  <Badge
                    color="error"
                    badgeContent={itemCount || 0}
                    invisible={!itemCount}
                  >
                    <Icon fontSize="small">shopping_cart</Icon>
                  </Badge>
                }
              >
                {t("common.cart", "Cart")}
              </MDButton>

              <MDBox
                sx={{
                  flex: { xs: "0 0 150px", md: "0 0 280px" },
                  width: { xs: 150, md: 280 },
                  maxWidth: { md: 280 },
                  display: { xs: isHomePage ? "none" : "block", md: "block" },
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  placeholder={
                    searchPlaceholder || t("common.searchPlaceholder")
                  }
                  value={q}
                  onChange={(e) => onChangeQ(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") submitSearch(q);
                  }}
                  sx={{
                    width: "100%",
                    bgcolor: theme.canvas,
                    borderRadius: `${theme.buttonRadius}px`,
                    "& .MuiInputBase-root": { width: "100%" },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: `${theme.buttonRadius}px`,
                      fontSize: "0.88rem",
                      "& fieldset": { borderColor: theme.line },
                      "&:hover fieldset": { borderColor: accent },
                      "&.Mui-focused fieldset": { borderColor: accent },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon sx={{ color: "#666666" }}>search</Icon>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          aria-label="Search"
                          onClick={() => submitSearch(q)}
                        >
                          <Icon fontSize="small">arrow_forward</Icon>
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </MDBox>

              {isAuthed && portalUser ? (
                <>
                  <MDButton
                    variant="contained"
                    sx={{
                      bgcolor: theme.ink,
                      color: "#fff",
                      borderRadius: `${theme.buttonRadius}px`,
                      px: 2,
                      py: 0.9,
                      fontSize: "0.82rem",
                      fontWeight: 800,
                      boxShadow: "none",
                      "&:hover": { bgcolor: accent, boxShadow: "none" },
                    }}
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    endIcon={<Icon fontSize="small">expand_more</Icon>}
                  >
                    {distributor
                      ? t("common.portal", "Portal")
                      : t("common.account", "Account")}
                  </MDButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={openMenu}
                    onClose={() => setAnchorEl(null)}
                  >
                    {!distributor ? (
                      <MenuItem
                        onClick={() => {
                          setAnchorEl(null);
                          navigate("/account");
                        }}
                      >
                        {t("portal.accountHome", "Account")}
                      </MenuItem>
                    ) : null}
                    <MenuItem
                      onClick={() => {
                        setAnchorEl(null);
                        navigate("/account/valve-bodies");
                      }}
                    >
                      {t("portal.registeredUnits", "Registered Items")}
                    </MenuItem>
                    {!distributor ? (
                      <MenuItem
                        onClick={() => {
                          setAnchorEl(null);
                          navigate("/account/orders");
                        }}
                      >
                        {t("portal.orders", "Orders")}
                      </MenuItem>
                    ) : null}
                    <MenuItem
                      onClick={() => {
                        setAnchorEl(null);
                        navigate("/account/profile");
                      }}
                    >
                      {t("portal.profileBilling", "Profile / Billing")}
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setAnchorEl(null);
                        navigate("/account/addresses");
                      }}
                    >
                      {t("portal.addresses", "Addresses")}
                    </MenuItem>
                    <Divider />
                    <MenuItem
                      onClick={() => {
                        setAnchorEl(null);
                        navigate("/sign-out");
                      }}
                    >
                      {t("common.signOut", "Sign out")}
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <MDButton
                  variant="contained"
                  sx={{
                    bgcolor: theme.ink,
                    color: "#fff",
                    borderRadius: `${theme.buttonRadius}px`,
                    px: 2,
                    py: 0.9,
                    fontSize: "0.82rem",
                    fontWeight: 800,
                    boxShadow: "none",
                    "&:hover": { bgcolor: accent, boxShadow: "none" },
                  }}
                  onClick={goToSessionDestination}
                >
                  {isAuthed
                    ? t("common.continue", "Continue")
                    : t("common.signIn", "Sign in")}
                </MDButton>
              )}
            </MDBox>
          </Toolbar>
        </AppBar>
      ) : null}

      {pageBody}
    </Box>
  );
}

StorefrontLayout.propTypes = {
  children: PropTypes.node.isRequired,
  searchPlaceholder: PropTypes.string,
  // Set to null to disable container entirely (for full-bleed hero sections).
  maxWidth: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
    PropTypes.oneOf([null]),
  ]),
  hideHeader: PropTypes.bool,
};

StorefrontLayout.defaultProps = {
  searchPlaceholder: null,
  maxWidth: "lg",
  hideHeader: false,
};
